/**
 * Builds Authentik RP-initiated logout (end-session / invalidation) URLs from Vite env.
 * Uses the same post-logout path rules as the sidebar: app origin from
 * `VITE_AUTHENTIK_REDIRECT_URI` with `/callback` stripped, then `/authentikLogin`.
 */
import { ENV } from '@/config/env';

export type BuildAuthentikEndSessionUrlOptions = {
  /**
   * OIDC ID token JWT from the authorization code token response.
   * Must not be set to `client_id`. Omit when unavailable (e.g. no in-memory token).
   */
  idTokenHint?: string;
};

const AUTH_ID_TOKEN_STORAGE_KEY = "auth_id_token";

/**
 * Reads the most recent OIDC id_token from session storage if available.
 */
function getStoredIdTokenHint(): string | undefined {
  const token = sessionStorage.getItem(AUTH_ID_TOKEN_STORAGE_KEY)?.trim();
  return token || undefined;
}

/**
 * Resolves `post_logout_redirect_uri` from env (SideBar-equivalent logic).
 */
export function getAuthentikPostLogoutRedirectUri(): string {
  const redirectUri = ENV.AUTHENTIK.REDIRECT_URI;
  if (redirectUri) {
    return `${redirectUri.replace(/\/callback\/?$/, "")}/authentikLogin?redirectTo=%2F`;
  }
  return `${window.location.origin}/authentikLogin?redirectTo=%2F`;
}

/**
 * Constructs the Authentik end-session URL with `client_id` and `post_logout_redirect_uri`.
 * Adds `id_token_hint` only when a non-empty JWT is provided.
 *
 * @param options - Optional `idTokenHint` (real ID token only).
 * @returns Parsed URL or `null` if required env vars are missing.
 */
export function buildAuthentikEndSessionUrl(
  options?: BuildAuthentikEndSessionUrlOptions,
): URL | null {
  const explicitEndSessionUrl = ENV.AUTHENTIK.END_SESSION_URL;

  const endSessionBase = explicitEndSessionUrl?.trim();
  const clientId = ENV.AUTHENTIK.CLIENT_ID;
  if (!endSessionBase?.trim() || !clientId?.trim()) {
    return null;
  }

  const url = new URL(String(endSessionBase));
  const postLogoutRedirectUri = getAuthentikPostLogoutRedirectUri();

  // OIDC end-session endpoint supports post_logout_redirect_uri and client_id.
  if (url.pathname.includes("/end-session")) {
    url.searchParams.set("client_id", String(clientId));
    url.searchParams.set("post_logout_redirect_uri", postLogoutRedirectUri);
  } else {
    // Legacy flow endpoint fallback: forward to app login bootstrap after logout.
    // Authentik flow URLs typically use `next` instead of OIDC logout parameters.
    url.searchParams.set("next", postLogoutRedirectUri);
  }

  const hint = options?.idTokenHint?.trim() || getStoredIdTokenHint();
  if (hint && url.pathname.includes("/end-session")) {
    url.searchParams.set("id_token_hint", hint);
  }

  return url;
}
